<template>
    <div
        class="input-color-select"
    >
        <div
            v-if="!hideSelectedLabel"
            class="py-1"
        >
            <label
                :for="id"
                class="input-color-select-label d-flex flex-row align-center my-2"
            >
                <template
                    v-if="model"
                >
                    <ColorSwatch
                        :color="model.colors"
                        disable-border
                    ></ColorSwatch>
                    <span
                        class="ml-2"
                    >
                        {{ model.name }}
                    </span>
                </template>
                <div
                    v-else
                    class="input-color-no-value"
                ></div>
            </label>
        </div>
        <div
            class="input-color-select-items color-inline-btn-group-input"
        >
            <v-item-group
                class="d-flex flex-wrap"
                :id="id"
                :model-value="model"
                @update:model-value="onChange($event)"
                mandatory
            >
                <v-item
                    v-for="colorObj in colors"
                    :key="colorObj.uid"
                    :value="colorObj"
                    #default="{ isSelected, toggle, value }"
                >
                    <ColorSwatchButton
                        :key="`color-btn-option-${colorObj.uid}`"
                        variant="text"
                        :model-value="isSelected"
                        :color="colorObj.colors"
                        :aria-label="colorObj.name"
                        :rounded="false"
                        :class="{
                            'option-off': disabledItems?.has(colorObj.uid)
                        }"
                        :disabled="disabledItems?.has(colorObj.uid)"
                        @click="toggle"
                    ></ColorSwatchButton>
                </v-item>
            </v-item-group>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import ColorSwatch from '~/components/color/ColorSwatch/ColorSwatch.vue';
    import ColorSwatchButton from '~/components/color/ColorSwatchButton/ColorSwatchButton.vue';
    import type { AttributeValue } from '~/types';
    import { pausableFilter, watchWithFilter } from '@vueuse/core';

    const props = defineProps<{
        modelValue: Nullable<string>;
        colors: AttributeValue[];
        disabled?: boolean;
        disabledItems?: Set<string>;
        mandatory?: boolean;
        hideSelectedLabel?: boolean;
    }>();

    const emit = defineEmits<{
        'update:modelValue': [value: Nullable<string>];
    }>();

    const {
        eventFilter,
        pause,
        resume
    } = pausableFilter();

    const id = useId();
    const model = ref<Nilish<AttributeValue>>();

    const unWatchProps = watchWithFilter(
        () => props.modelValue,
        (newVal) => {
            const defaultFirst = props.colors[0];
            model.value = newVal ? props.colors.find((o) => o.uid === newVal) : defaultFirst || defaultFirst;
        },
        {
            eventFilter,
            immediate: true
        }
    );

    function onChange(colorObj: Nullable<AttributeValue>) {
        if (!props.disabled) {
            model.value = colorObj;
            setValue(colorObj);
        }
    }

    function setValue(updatedModel: Nullable<AttributeValue>): void {
        pause();
        const value = updatedModel?.uid ?? null;
        emit('update:modelValue', value);
        setTimeout(() => resume?.(), 250);
    }

    onBeforeUnmount(() => {
        unWatchProps();
    });
</script>

<style lang="scss">
.input-color-select-items {
    --color-inline-btn-gap: 0.6rem;
    --color-inline-btn-padding: 4px;
    --color-inline-btn-size: 36px;
}
</style>
